import React, { useState } from "react";
import CardHESP from "../../CardHESP/CardHESP";
import CardHESPTitle from "../../CardHESP/CardHESPTitle";

import { Button, Grid, IconButton, Tooltip } from "@mui/material";
import { CopyAll, Save } from "@mui/icons-material";
import HESPFormLabel from "../../HESPFormLabel/HESPFormLabel";
import styled from "styled-components";
import { copyToClipboard } from "../../CopyLabel/CopyLabel";
import AuthService from "../../../auth/AuthService";
import { useMutation } from "react-apollo";
import { GET_CHANNEL_QUERY, UPDATE_STREAM_CONFIG_MUTATION } from "../Overview/channelQueriesMutations";
import { toast } from "react-toastify";
import { TOAST_SETTINGS } from "../../BillingDetails/AddPaymentMethodDialog";
import { GetChannelQuery_channel } from "../Overview/__generated__/GetChannelQuery";
import { ChannelStatusType } from "../../../__generated__/globalTypes";
import {
    UpdateStreamConfigMutation,
    UpdateStreamConfigMutationVariables,
} from "../Overview/__generated__/UpdateStreamConfigMutation";
import ChannelRestreamDestinations, { ReStreamDestination } from "../Create/ChannelRestreamDestinations";

const Wrapper = styled.div`
    margin-top: 8px;
`;

const InputContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Input = styled.input.attrs((props: { disabled: boolean }) => ({
    disabled: props.disabled,
}))`
    font-size: 14px;
    border: 1px solid ${props => props.theme.lightGrayBottom};
    border-radius: 6px;
    padding: 6px 10px;
    outline: rgba(0, 0, 0, 0.6);
    color: #696969;
    width: 100%;
    cursor: ${props => (props.disabled === true ? "not-allowed" : "text")};
`;

interface Props {
    channel: GetChannelQuery_channel;
}

function ChannelStreamSettings ({ channel }: Props) {
    const [disabled] = useState<boolean>(channel.tla?.enabled === false);
    const [publishUrl, setPublishUrl] = useState<string>(channel.streamConfig.publishUrl ?? "");
    const [restreamUrls, setRestreamUrls] = useState<ReStreamDestination[]>(channel.streamConfig.restream ?? []);
    const [needsSave, setNeedsSave] = useState<boolean>(false);

    const [updateStreamConfigMut] = useMutation<
        UpdateStreamConfigMutation,
        UpdateStreamConfigMutationVariables
    >(UPDATE_STREAM_CONFIG_MUTATION);

    async function onSave () {
        if (!isValid() || disabled) return;
        try {
            const { abr, fps, bitrate, resolution, drm, bitrateMode, skipTranscode} = channel.streamConfig;
            await updateStreamConfigMut({
                variables: {
                    input: {
                        channelId: channel.channelId,
                        organizationId: channel.organizationId,
                        bitrate,
                        resolution,
                        abr,
                        fps,
                        publishUrl,
                        drm: drm?.enabled ?? false,
                        bitrateMode,
                        restream : restreamUrls,
                        skipTranscode : skipTranscode ?? false
                    },
                },
                refetchQueries: [
                    {
                        query: GET_CHANNEL_QUERY,
                        variables: {
                            channelId: channel.channelId,
                            organizationId: channel.organizationId,
                        },
                    },
                ],
            });
            setNeedsSave(false);
            toast.success("🚀 Stream URL updated", TOAST_SETTINGS);
        } catch (error: any) {
            toast.error(error.graphQLErrors[0].message ?? "⛔️ Failed to udpate the stream URL", TOAST_SETTINGS);
        }
    }

    function isValid (): boolean {
        // We only want to set this _in case_ this was previously set
        // Additionally, do not allow updating if the user isn't admin + the stream is running
        if (disabled) {
            return false;
        }
        const isAdmin = AuthService.isAdmin() ?? false;
        const isTlaChannel = channel.tla?.enabled === true;
        if (!isAdmin || !isTlaChannel) {
            return false;
        }

        const isChannelStopped = channel.channelStatus === ChannelStatusType.stopped;
        return isChannelStopped;
    }

    function onChangePublishUrl (value: string) {
        if (value === publishUrl) return;
        setPublishUrl(value);
        setNeedsSave(true);
    }

    function onRestreamChange (newDestinations: ReStreamDestination[]) {
        setRestreamUrls(newDestinations);
        setNeedsSave(true);
    }

    return (
        <CardHESP>
            <CardHESPTitle
                title='Outbound configuration'
                subtitle='Only applicable for on-prem encoder'
                button={
                    needsSave && !disabled ? (
                        <Grid container spacing={2}>
                            <Grid item>
                                <Button
                                    startIcon={<Save />}
                                    variant='contained'
                                    color='primary'
                                    size='small'
                                    disabled={!isValid()}
                                    onClick={onSave}
                                ></Button>
                            </Grid>
                        </Grid>
                    ) : null
                }
            />
            {/* Tim Question -> Would this be better off as a standalone component */}
            <Wrapper>
                <HESPFormLabel label='Stream name' size='medium' />
                <InputContainer>
                    <Input
                        type='text'
                        value={publishUrl}
                        onChange={e => onChangePublishUrl(e.target.value)}
                        disabled={disabled}
                    />
                    <Tooltip title='Copy'>
                        <IconButton size='small' onClick={() => copyToClipboard(publishUrl)}>
                            <CopyAll fontSize='small' />
                        </IconButton>
                    </Tooltip>
                </InputContainer>
                <HESPFormLabel label='Restream configuration' size='medium' />

                <ChannelRestreamDestinations destinations={restreamUrls} onChange={onRestreamChange} />
            </Wrapper>
        </CardHESP>
    );
}

export default ChannelStreamSettings;
